// Two Cognito pools:
// Enterprise pool — used by admin.privacy.education and app.privacy.education
// Public pool    — used by public.privacy.education

const ENTERPRISE_POOL_ID = 'ca-central-1_wIuxbBJ06';
const PUBLIC_POOL_ID     = 'ca-central-1_VWFHgLyOI';

export const enterprisePool = {
  userPoolId:    ENTERPRISE_POOL_ID,
  clientId:      '2fbc48ffaeefapjb25p722v80d',
  cognitoDomain: 'https://ca-central-1wiuxbbj06.auth.ca-central-1.amazoncognito.com',
};

export const publicPool = {
  userPoolId:    PUBLIC_POOL_ID,
  clientId:      '59k3j90mrs4jip9frtntqbc7kj',
  cognitoDomain: 'https://public-privacy-education.auth.ca-central-1.amazoncognito.com',
};

export type Pool        = 'enterprise' | 'public';
export type AdminRole   = 'super_admin' | 'admin' | 'org_admin';
export type AccessLevel =
  | 'superadmin_full'       // enterprise pool + super_admin  → sees both platforms
  | 'admin_enterprise'      // enterprise pool + admin        → enterprise only
  | 'orgadmin_enterprise'   // enterprise pool + org_admin    → their enterprise org
  | 'superadmin_public'     // public pool    + super_admin  → public platform admin
  | 'orgadmin_public';      // public pool    + org_admin    → their public org

export function parseJWT(token: string): any {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
}

export function detectPool(token: string): Pool | null {
  const payload = parseJWT(token);
  if (!payload?.iss) return null;
  if (payload.iss.includes(ENTERPRISE_POOL_ID)) return 'enterprise';
  if (payload.iss.includes(PUBLIC_POOL_ID))     return 'public';
  return null;
}

export function getStoredToken(): string | null {
  return localStorage.getItem('pe_admin_token');
}

export function isTokenValid(token: string): boolean {
  const payload = parseJWT(token);
  return !!(payload && payload.exp * 1000 > Date.now());
}

export function storeSession(idToken: string): void {
  const pool = detectPool(idToken);
  if (!pool) return;
  const payload = parseJWT(idToken);
  if (!payload) return;
  localStorage.setItem('pe_admin_token', idToken);
  localStorage.setItem('pe_admin_pool', pool);
  localStorage.setItem('pe_admin_email', payload.email || '');
  const name = payload.name || payload.given_name || payload['cognito:username'] || '';
  localStorage.setItem('pe_admin_name', name);
  const orgId = payload['custom:orgId'] || '';
  if (orgId) localStorage.setItem('pe_admin_org_id', orgId);
}

export function clearSession(): void {
  localStorage.removeItem('pe_admin_token');
  localStorage.removeItem('pe_admin_pool');
  localStorage.removeItem('pe_admin_email');
  localStorage.removeItem('pe_admin_name');
  localStorage.removeItem('pe_admin_org_id');
}

export function getPool(): Pool | null {
  return (localStorage.getItem('pe_admin_pool') as Pool) || null;
}

export function getUserEmail(): string {
  return localStorage.getItem('pe_admin_email') || '';
}

export function getUserName(): string {
  return localStorage.getItem('pe_admin_name') || '';
}

export function getOrgId(): string | null {
  return localStorage.getItem('pe_admin_org_id') || null;
}

function getGroups(): string[] {
  const token = getStoredToken();
  if (!token) return [];
  return parseJWT(token)?.['cognito:groups'] || [];
}

export function getRole(): AdminRole | null {
  const groups = getGroups();
  if (groups.includes('super_admin')) return 'super_admin';
  if (groups.includes('admin'))       return 'admin';
  if (groups.includes('org_admin'))   return 'org_admin';
  return null;
}

export function getAccessLevel(): AccessLevel | null {
  const token = getStoredToken();
  if (!token || !isTokenValid(token)) return null;
  const pool = getPool();
  const role = getRole();
  if (!pool || !role) return null;
  if (pool === 'enterprise' && role === 'super_admin') return 'superadmin_full';
  if (pool === 'enterprise' && role === 'admin')       return 'admin_enterprise';
  if (pool === 'enterprise' && role === 'org_admin')   return 'orgadmin_enterprise';
  if (pool === 'public'     && role === 'super_admin') return 'superadmin_public';
  if (pool === 'public'     && role === 'org_admin')   return 'orgadmin_public';
  return null;
}

export function isAdminUser(): boolean {
  return getAccessLevel() !== null;
}

export function isSuperAdmin(): boolean {
  return getRole() === 'super_admin';
}

function adminRedirectUri(): string {
  return window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : 'https://admin.privacy.education';
}

export function getEnterpriseLoginURL(): string {
  const { cognitoDomain, clientId } = enterprisePool;
  const scopes = encodeURIComponent('email openid phone');
  const uri    = encodeURIComponent(adminRedirectUri());
  return `${cognitoDomain}/login?client_id=${clientId}&response_type=token&scope=${scopes}&redirect_uri=${uri}`;
}

export function getPublicLoginURL(): string {
  const { cognitoDomain, clientId } = publicPool;
  const scopes = encodeURIComponent('email openid profile');
  const uri    = encodeURIComponent(adminRedirectUri());
  return `${cognitoDomain}/login?client_id=${clientId}&response_type=token&scope=${scopes}&redirect_uri=${uri}`;
}

export function getLogoutURL(): string {
  const pool = getPool();
  const { cognitoDomain, clientId } = pool === 'public' ? publicPool : enterprisePool;
  const uri = encodeURIComponent(adminRedirectUri());
  return `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${uri}`;
}

// Legacy — some callers pass token as argument; keep for backwards compat
export function isAdminUserToken(token: string): boolean {
  if (!token || !isTokenValid(token)) return false;
  const pool    = detectPool(token);
  const payload = parseJWT(token);
  const groups: string[] = payload?.['cognito:groups'] || [];
  if (pool === 'enterprise') return groups.some(g => ['admin','super_admin','org_admin'].includes(g));
  if (pool === 'public')     return groups.some(g => ['super_admin','org_admin'].includes(g));
  return false;
}
